<template>
  <div class="captcha-wrapper">
    <div :id="captchaId" class="captcha-root"></div>
    <button :id="triggerId" type="button" class="captcha-hidden-trigger"></button>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

type CaptchaResult = {
  captchaVerifyParam: string
  sceneId: string
}

const props = withDefaults(
  defineProps<{
    container?: string
    sceneId?: string
    height?: number
  }>(),
  {
    container: '',
    sceneId: '',
    height: 48,
  }
)

const emit = defineEmits<{
  success: [CaptchaResult]
  fail: [unknown]
  error: [unknown]
}>()

const uid = Math.random().toString(36).slice(2, 10)
const captchaId = ref(`aliyun-captcha-${uid}`)
const triggerId = ref(`aliyun-captcha-trigger-${uid}`)

const captcha = ref<AliyunCaptchaInstance | null>(null)
const data = ref<CaptchaResult | null>(null)
const success = ref(false)

const resetCaptcha = () => {
  if (!captcha.value) return
  if (typeof captcha.value.reset === 'function') {
    captcha.value.reset()
    return
  }
  if (typeof captcha.value.refresh === 'function') {
    captcha.value.refresh()
  }
}

const init = (forceRecreate = false) => {
  if (forceRecreate && captcha.value) {
    captcha.value = null
    const root = document.getElementById(captchaId.value)
    if (root) {
      root.innerHTML = ''
    }
  }

  data.value = null
  success.value = false

  if (captcha.value) {
    resetCaptcha()
    return
  }

  const sceneId = props.sceneId || window.__ALIYUN_CAPTCHA_SCENE_ID__ || ''
  if (!sceneId) {
    emit('error', 'missing-scene-id')
    return
  }

  if (!window.initAliyunCaptcha || !window.AliyunCaptchaConfig?.prefix) {
    emit('error', 'captcha-sdk-not-ready')
    return
  }

  let width = 400
  const height = Math.max(props.height, 32)
  const el = props.container ? document.querySelector(props.container) : null

  if (el) {
    const style = window.getComputedStyle(el)
    const containerWidth = parseInt(style.width, 10)
    if (Number.isFinite(containerWidth) && containerWidth > 0) {
      width = containerWidth
    }
  }

  window.initAliyunCaptcha({
    SceneId: sceneId,
    mode: 'embed',
    element: `#${captchaId.value}`,
    button: `#${triggerId.value}`,
    language: 'cn',
    slideStyle: {
      width: Math.max(width, 320),
      height,
    },
    success(captchaVerifyParam) {
      if (success.value) return
      const result = {
        captchaVerifyParam,
        sceneId,
      }
      data.value = result
      success.value = true
      if (captcha.value && typeof captcha.value.hide === 'function') {
        captcha.value.hide()
      }
      emit('success', result)
    },
    fail(result) {
      success.value = false
      emit('fail', result)
    },
    onError(errorCode) {
      success.value = false
      emit('error', errorCode)
    },
    getInstance(instance) {
      captcha.value = instance
    },
  })
}

onMounted(() => {
  init()
})

defineExpose({ init })
</script>

<style lang="scss">
.captcha-wrapper {
  width: 100%;
  overflow: visible;
}

.captcha-root {
  width: 100%;
  min-height: 48px;
  overflow: visible;
}

.captcha-hidden-trigger {
  display: none;
}
</style>
